// src/pages/Home.js
import React from 'react';
import Pricing from '../components/Pricing';
import { Link } from 'react-router-dom';
import Seo from '../components/Seo';
import builtForShopifySocialWide from '../assets/built-for-shopify-media-kit-2025/Social media share graphic/social-built-for-shopify-16-9.png';
import builtForShopifySocialSquare from '../assets/built-for-shopify-media-kit-2025/Social media share graphic/social-built-for-shopify-1-1.png';
import './Home.css';

const Home = () => {
  return (
    <div className="home">
      <Seo
        title="Clean Sweeps | Shopify Sweepstakes App"
        description="Clean Sweeps is a Shopify sweepstakes platform and app to run and manage sweepstakes with purchase-based entry rules, AMOE compliance, referrals, and storefront widgets."
        path="/"
        image="/og/home.png"
        imageAlt="Clean Sweeps Shopify sweepstakes overview"
      />
      <section className="home-hero">
        <div className="home-hero__inner">
          <div className="home-hero__content">
            <p className="home-eyebrow">
              Shopify sweepstakes • Built for Shopify
            </p>
            <h1>Run sweepstakes on Shopify and show entries everywhere.</h1>
            <p className="home-hero__lead">
              Clean Sweeps helps you configure purchase-based entries and
              surface them across product, cart, checkout, and post-purchase
              experiences. As a Built for Shopify app, Clean Sweeps is designed
              to meet Shopify&apos;s highest standards for quality, trust, and
              performance.
            </p>
            <div className="home-hero__actions">
              <a
                href="https://apps.shopify.com/clean-sweeps"
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn--primary"
              >
                See Our App On Shopify
              </a>
              <a href="#pricing" className="btn btn--ghost">
                View Pricing
              </a>
            </div>
          </div>
          <div className="home-hero__panel">
            <p className="home-panel__eyebrow">
              Storefront visibility and merchant trust
            </p>
            <ul className="home-panel__list">
              <li>
                <strong>Built for Shopify:</strong> Recognized by Shopify for
                trust, speed, and merchant-first app quality.
              </li>
              <li>
                <strong>Product pages:</strong> Product Entry Widget shows
                entries per item.
              </li>
              <li>
                <strong>Cart:</strong> Cart Widget shows entries earned before
                checkout.
              </li>
              <li>
                <strong>Checkout line items:</strong> Checkout Product Entries
                shows entries per item.
              </li>
              <li>
                <strong>Checkout summary:</strong> Checkout Total Summary shows
                total entries for the order.
              </li>
              <li>
                <strong>Thank-you page:</strong> Thank-You Page Summary confirms
                earned entries.
              </li>
              <li>
                <strong>Customer accounts:</strong> Account Widget shows entries
                tied to the customer.
              </li>
              <li>
                <strong>Guest lookup:</strong> Guest Email Lookup Widget shows
                entries for guest checkouts.
              </li>
            </ul>
          </div>
        </div>
      </section>

      <section className="home-guide">
        <div className="home-section__header">
          <p className="home-eyebrow">Guide</p>
          <h2>How to run a sweepstakes on Shopify.</h2>
          <p className="home-section__lead">
            Learn how to plan, launch, and manage sweepstakes with a Shopify
            sweepstakes app and platform built for compliance and visibility.
          </p>
        </div>
        <div className="home-guide__grid">
          <article className="home-guide__card">
            <h3>Plan the promotion</h3>
            <p>
              Define the prize, official rules, eligibility, and AMOE workflow
              before launch.
            </p>
          </article>
          <article className="home-guide__card">
            <h3>Configure entry rules</h3>
            <p>
              Set entries-per-$, bonus periods, product eligibility, and
              multipliers to match your strategy.
            </p>
          </article>
          <article className="home-guide__card">
            <h3>Manage sweepstakes</h3>
            <p>
              Track entrants, select winners, and export records for reporting
              and audits.
            </p>
          </article>
        </div>
        <div className="home-guide__actions">
          <Link
            to="/how-to-run-a-sweepstakes"
            className="btn btn--primary home-guide__featured-button"
          >
            Read Brian&apos;s guide
          </Link>
          <Link to="/resources" className="btn btn--ghost">
            Browse resources
          </Link>
        </div>
        <p className="home-guide__note">
          Low dropshipping sales?{" "}
          <Link to="/dropshipping-sweepstakes">Run a sweepstakes.</Link>
        </p>
      </section>

      <section
        id="built-for-shopify"
        className="home-bfs"
        aria-labelledby="home-bfs-title"
      >
        <div className="home-bfs__inner">
          <div className="home-bfs__content">
            <p className="home-eyebrow">Built for Shopify</p>
            <h2 id="home-bfs-title">
              Clean Sweeps is officially Built for Shopify.
            </h2>
            <p className="home-bfs__lead">
              This status means Clean Sweeps met Shopify&apos;s highest
              standards for quality, trust, speed, and merchant experience.
            </p>
            <ul className="home-bfs__list">
              <li>
                <strong>Trustworthy:</strong> Merchants can rely on an app
                reviewed for strong security and reliability expectations.
              </li>
              <li>
                <strong>Fast and easy to use:</strong> Built for Shopify apps
                are expected to deliver performant, streamlined experiences.
              </li>
              <li>
                <strong>Brand credibility:</strong> The badge reinforces that
                Clean Sweeps is built to help merchants drive measurable
                business outcomes.
              </li>
            </ul>
            <div className="home-bfs__actions">
              <a
                href="https://apps.shopify.com/clean-sweeps"
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn--primary"
              >
                View on Shopify App Store
              </a>
            </div>
          </div>
          <div className="home-bfs__media">
            <figure className="home-bfs__social-card">
              <picture>
                <source
                  media="(max-width: 640px)"
                  srcSet={builtForShopifySocialSquare}
                />
                <img
                  src={builtForShopifySocialWide}
                  alt="Clean Sweeps Built for Shopify promotional graphic"
                  loading="lazy"
                />
              </picture>
            </figure>
          </div>
        </div>
      </section>

      <Pricing />

      <section className="home-cta">
        <div className="home-cta__inner">
          <h2>Ready to show entries across your storefront?</h2>
          <p>
            Install Clean Sweeps or connect with our team to plan your next
            sweepstakes.
          </p>
          <div className="home-cta__actions">
            <a
              href="https://apps.shopify.com/clean-sweeps"
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn--primary"
            >
              Install Clean Sweeps
            </a>
            <Link to="/consulting" className="btn btn--ghost">
              Talk With Our Team
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
